import { GET_LIST, GET_ONE, GET_MANY, GET_MANY_REFERENCE, CREATE, UPDATE, DELETE, fetchUtils } from 'admin-on-rest'
import { stringify } from 'query-string'
import _ from 'lodash'
import { truncate } from './utils/query';

const API_URL = `${process.env.REACT_APP_API_URL}/marketing`
const ALLOWED_FIELDS_UPDATE = {
  offers: ['description', 'smallIcon', 'productIcon', 'titleBackground'],
  packages: ['productIcon', 'price', 'discount'],
  shops: ['privateBasePrice', 'publicBasePrice', 'sections'],
  countries: ['currency', 'priceOverwrite'],
  translations: ['values'],
  products: ['identifier', 'title', 'icon', 'previewImage', 'inputFields', 'contentItems', 'questionsAmount', 'description', 'themeColor', 'styles', 'type', 'initialConversationMessage', 'payoutRates'],
  packageTemplates: ['creditValue', 'ritualTemplate', 'inAppPurchaseAndroid', 'inAppPurchaseIos', 'oneTime', 'featured', 'tracking', 'productIcon', 'price', 'localizedIdentifier']
}

export async function searchPackages(productIdentifier, country, platform) {
  const query = {
    country,
    ...(platform ? { platform } : {}),
  }

  if (productIdentifier) {
    query.productIdentifier = productIdentifier
  }

  const response = await fetch(`${API_URL}/packages/all?query=${JSON.stringify(query)}`, getDefaultFetchOptions())
  const jsonResult = await response.json()
  return jsonResult.map((packageItem) => ({...packageItem, title: packageItem.productIdentifier}))
}

export async function searchCountries(text) {
  const response = await fetch(`${API_URL}/countries/search?query=${text}`, getDefaultFetchOptions())
  const jsonResult = await response.json()
  return jsonResult.map((country) => ({...country, title: `${truncate(country.name, 30)} (${country.code})`}))
}

export async function getLanguages() {
  const query = {available: true}
  const response = await fetch(`${API_URL}/languages?query=${JSON.stringify(query)}`, getDefaultFetchOptions())
  const jsonResult = await response.json()
  return jsonResult.map((languageItem) => languageItem.iso)
}

export async function getRituals() {
  const response = await fetch(`${API_URL}/products/all`, getDefaultFetchOptions())
  const jsonResult = await response.json()
  return jsonResult.map((ritual) => ({ identifier: ritual.identifier, title: ritual.title }))
}

export async function searchOffers(title) {
  const query = { title }
  const response = await fetch(`${API_URL}/offers/all?query=${JSON.stringify(query)}`, getDefaultFetchOptions())
  return response.json()
}

export async function getPackageTemplates() {
  const response = await fetch(`${API_URL}/packageTemplates/all`, getDefaultFetchOptions())
  return response.json()
}

function getDefaultFetchOptions() {
  return { headers: getDefaultHeaders() }
}

function getDefaultHeaders() {
  const token = localStorage.getItem('token')
  return new Map([['x-adviqo-platform', 'web'], ['Authorization', `JWT ${token}`]])
}

/**
 * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
 * @param {String} resource Name of the resource to fetch, e.g. 'posts'
 * @param {Object} params The REST request params, depending on the type
 * @returns {Object} { url, options } The HTTP request parameters
 */
const convertRESTRequestToHTTP = (type, resource, params) => {
  let url = ''

  const options = getDefaultFetchOptions()

  switch (type) {
    case GET_LIST: {
      const { page, perPage } = params.pagination
      const { field, order } = params.sort
      // sorting
      const sort = {}
      sort[field] = order === 'ASC' ? 1 : -1

      const query = {
        sort: JSON.stringify(sort),
        skip: (page - 1) * perPage,
        limit: perPage,
        query: JSON.stringify(params.filter),
      }

      url = `${API_URL}/${resource}?${stringify(query)}`
      break
    }
    case GET_ONE:
      if (resource === 'currencies') {
        const query = {
          query: JSON.stringify({ iso: params.id }),
        }
        url = `${API_URL}/${resource}?${stringify(query)}`
      } else {
        url = `${API_URL}/${resource}/${params.id}`
      }
      break
    case GET_MANY: {
      const query = {
        query: JSON.stringify({ _id: { $in: params.ids } }),
      }
      url = `${API_URL}/${resource}?${stringify(query)}`
      break
    }
    case GET_MANY_REFERENCE: {
      const { field, order } = params.sort
      const sort = {}
      sort[field] = order === 'ASC' ? 1 : -1
      const query = {
        sort: JSON.stringify(sort),
      }
      url = `${API_URL}/${resource}?${stringify(query)}`
      break
    }
    case UPDATE:
      url = `${API_URL}/${resource}/${params.id}`
      options.method = 'PATCH'
      const data = ALLOWED_FIELDS_UPDATE[resource]
          ? _.pick(params.data, ALLOWED_FIELDS_UPDATE[resource])
          : params.data
      options.body = JSON.stringify(data)
      break
    case CREATE:
      url = `${API_URL}/${resource}`
      options.method = 'POST'
      options.body = JSON.stringify(params.data)
      break
    case DELETE:
      url = `${API_URL}/${resource}/${params.id}`
      options.method = 'DELETE'
      break
    default:
      throw new Error(`Unsupported fetch action type ${type}`)
  }

  return { url, options }
}

/**
 * @param {Object} response HTTP response from fetch()
 * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
 * @param {String} resource Name of the resource to fetch, e.g. 'posts'
 * @param {Object} params The REST request params, depending on the type
 * @returns {Object} REST response
 */
const convertHTTPResponseToREST = (response, type, resource, params) => {
  const { json } = response

  switch (type) {
    case GET_ONE:
      let jsonObj = json
      if (json && Array.isArray(json)) {
        jsonObj = json[0];
      } else if (json && json.data && Array.isArray(json.data)) {
        jsonObj = json.data[0];
      }

      return {
        data: {
          id: jsonObj.iso || jsonObj._id,
          ...jsonObj,
        }
      }
    case GET_LIST:
    case GET_MANY: {
      return {
        data: json.data,
        total: json.total,
      }
    }
    case CREATE:
      return { data: { ...params.data, id: json._id } }
    case DELETE:
      return { data: {} }
    default:
      return { data: json }
  }
}

export function sendCustomRequest(url, options) {
  const { fetchJson } = fetchUtils
  return fetchJson(`${process.env.REACT_APP_API_URL}/${url}`, options).then((response) => convertHTTPResponseToREST(response, 'CUSTOM_REQUEST', 'custom-request', {}))
}

/**
 * @param {string} type Request type, e.g GET_LIST
 * @param {string} resource Resource name, e.g. "posts"
 * @param {Object} payload Request parameters. Depends on the request type
 * @returns {Promise} the Promise for a REST response
 */
export default (type, resource, params) => {
  const { fetchJson } = fetchUtils
  const { url, options } = convertRESTRequestToHTTP(type, resource, params)
  return fetchJson(url, options).then((response) => convertHTTPResponseToREST(response, type, resource, params))
}



// WEBPACK FOOTER //
// ./src/restClient.js